// src/pages/projects/CancerProject.jsx
import React, { useState, useEffect } from "react";
import {
  Shield,
  Users,
  Heart,
  BookOpen,
  Phone,
  Home,
  AlertTriangle,
  TrendingUp,
} from "lucide-react";

const CancerProject = () => {
  const [animateIn, setAnimateIn] = useState(false);
  const [heroConsent, setHeroConsent] = useState(false);
  const [heroImgError, setHeroImgError] = useState(false);

  useEffect(() => setAnimateIn(true), []);

  // (Kept for parity with other pages; not rendered as stats bar here)
  const projectStats = [
    { number: "20,000", label: "People Reached (Awareness)", icon: Users },
    { number: "5,000", label: "Individuals Screened", icon: Heart },
    { number: "50", label: "Health Workers Trained", icon: Home },
    { number: "3", label: "Referral Partners", icon: Shield },
  ];

  const programPillars = [
    {
      icon: BookOpen,
      title: "Community Awareness & Mobilization",
      description:
        "Radio talk shows, community dialogues, health fairs, and IEC materials to improve knowledge on cancer risks, prevention, and early detection.",
      highlights: [
        "Radio + community dialogues across subcounties",
        "IEC materials in local languages",
        "Target: 20,000 community members",
      ],
    },
    {
      icon: Heart,
      title: "Screening Camps (Breast, Cervical, Prostate)",
      description:
        "Integrated community screening outreaches with onsite counselling, basic triage, and clear referral flow for positive findings.",
      highlights: [
        "Target: 5,000 screened in 24 months",
        "Breast self-exam education + clinical exams",
        "Cervical screening (HPV/visual inspection) & prostate risk assessment",
      ],
    },
    {
      icon: Users,
      title: "Capacity Building",
      description:
        "Training 50 community health workers and local providers on cancer education, screening protocols, documentation, and client navigation.",
      highlights: [
        "Standardized job aids & registers",
        "Supportive supervision & mentorship",
        "Quarterly review/learning sessions",
      ],
    },
    {
      icon: Shield,
      title: "Referral & Linkage System",
      description:
        "Structured referral pathways to Rakai Hospital, Masaka RRH, and the Uganda Cancer Institute with tracked outcomes and active follow-up.",
      highlights: [
        "Referral directory & contact points",
        "Navigation/transport support where feasible",
        "Case documentation and follow-up tracking",
      ],
    },
  ];

  const impactAreas = [
    {
      title: "Early Detection & Reduced Late Presentation",
      description:
        "More people screened earlier; fewer advanced-stage diagnoses at first contact.",
    },
    {
      title: "Community Knowledge & Prevention",
      description:
        "Improved awareness of risk factors, early warning signs, and timely care seeking.",
    },
    {
      title: "Stronger Referral & Continuity of Care",
      description:
        "Clear pathways to UCI and regional hospitals with monitored outcomes.",
    },
  ];

  return (
    <div className="project-page">
      {/* Bootstrap CSS */}
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css"
        rel="stylesheet"
      />

      {/* Urgent Banner */}
      <section
        className="urgent-banner"
        role="note"
        aria-label="Early screening callout"
      >
        <div className="container">
          <div className="urgent-content">
            <span className="urgent-icon" aria-hidden="true">
              <AlertTriangle size={20} />
            </span>
            <p className="urgent-text">
              <strong>Close the last-mile gap in cancer care.</strong> Fund
              community screening and strong referral pathways—so diagnosis
              happens in time, not at postmortem.
            </p>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background" />
        <div className="hero-overlay" />
        <div className="container position-relative">
          <div className="row align-items-center min-vh-80">
            <div className="col-lg-6">
              <div className="hero-content">
                <div className="hero-badge">Critical Initiative</div>
                <h1 className="hero-title">
                  Rakai Cancer Awareness, Screening & Referral Project
                </h1>
                <p className="hero-subtitle">RCASRP • RACOHI • 24 Months</p>
                <p className="hero-description">
                  Increasing community awareness, enabling early detection, and
                  strengthening referral linkages for breast, cervical, and
                  prostate cancers in Rakai District, Uganda.
                </p>
              </div>
            </div>

            {/* Image with Sensitive Content Disclaimer */}
            <div className="col-lg-6">
              <div className="hero-image-container">
                <div className={`project-image ${heroConsent ? "show" : "gate"}`}>
                  {!heroImgError ? (
                    <img
                      src="/images/projects/rcasrp.png"
                      alt="RCASRP Project"
                      className={`hero-image ${heroConsent ? "" : "blurred"}`}
                      onError={() => setHeroImgError(true)}
                      aria-hidden={!heroConsent}
                    />
                  ) : (
                    <div className="image-placeholder">
                      <Shield size={60} />
                      <span>Project Image</span>
                    </div>
                  )}

                  {/* Warning overlay (shown until consent) */}
                  {!heroConsent && (
                    <div
                      className="content-warning"
                      role="dialog"
                      aria-modal="false"
                      aria-labelledby="cw-title"
                      aria-describedby="cw-desc"
                    >
                      <div className="cw-icon" aria-hidden="true">
                        <AlertTriangle size={24} />
                      </div>
                      <h3 id="cw-title" className="cw-title">
                        Sensitive medical content
                      </h3>
                      <p id="cw-desc" className="cw-desc">
                        The image includes a sensitive medical image. Viewer
                        discretion advised.
                      </p>
                      <div className="cw-actions">
                        <button
                          type="button"
                          className="btn btn-light btn-sm"
                          onClick={() => setHeroConsent(true)}
                        >
                          I understand — show image
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Re-hide control when visible */}
                  {heroConsent && !heroImgError && (
                    <button
                      type="button"
                      className="rehide"
                      onClick={() => setHeroConsent(false)}
                      aria-label="Hide image and reapply sensitive content warning"
                    >
                      Hide image
                    </button>
                  )}
                </div>
              </div>
            </div>
            {/* /Image */}
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="overview-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="project-image-container">
                <img
                  src="/images/projects/rcasrp-overview.jpg"
                  alt="RCASRP Overview"
                  className="project-overview-image"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    e.currentTarget.nextElementSibling.style.display = "flex";
                  }}
                />
                <div className="image-placeholder">
                  <Heart size={60} />
                  <span>
                    Prevention of cervical cancer: Vaccination of 10–14 year old
                    girls with HPV vaccine
                  </span>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="overview-content">
                <div className="section-badge">Our Approach</div>
                <h2 className="section-title">Awareness → Screening → Referral</h2>
                <p className="section-description">
                  RCASRP addresses Uganda’s growing cancer burden in rural Rakai,
                  where local screening and diagnostic services are limited or
                  absent. The project combines community education, integrated
                  screening outreaches, capacity building for local health
                  workers, and strong referral pathways to UCI and regional
                  hospitals—driving early detection and improving treatment
                  outcomes.
                </p>

                <div className="impact-areas">
                  <h4 className="subsection-title">Core Focus Areas</h4>
                  {impactAreas.map((area, idx) => (
                    <div key={idx} className="impact-item">
                      <h5 className="impact-title">{area.title}</h5>
                      <p className="impact-description">{area.description}</p>
                    </div>
                  ))}
                </div>

                <div className="d-flex flex-wrap gap-2 mt-3">
                  <span className="badge rounded-pill text-bg-warning-subtle">
                    Cervical
                  </span>
                  <span className="badge rounded-pill text-bg-warning-subtle">
                    Breast
                  </span>
                  <span className="badge rounded-pill text-bg-warning-subtle">
                    Prostate
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Pillars */}
      <section className="pillars-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div
                className={`section-header text-center ${
                  animateIn ? "animate-in" : ""
                }`}
              >
                <div className="section-badge">Program Pillars</div>
                <h2 className="section-title">Four Pillars of Impact</h2>
                <p className="section-description">
                  A comprehensive strategy built on four interconnected pillars
                  that move communities from awareness to action and timely care.
                </p>
              </div>
            </div>
          </div>

          <div className="row">
            {programPillars.map((pillar, index) => (
              <div key={index} className="col-lg-6 mb-4">
                <div className="pillar-card">
                  <div className="pillar-icon">
                    <pillar.icon size={32} />
                  </div>
                  <div className="pillar-content">
                    <h3 className="pillar-title">{pillar.title}</h3>
                    <p className="pillar-description">{pillar.description}</p>
                    <ul className="pillar-highlights">
                      {pillar.highlights.map((h, idx) => (
                        <li key={idx}>
                          <TrendingUp size={16} />
                          {h}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency / Urgent CTA Section */}
      <section className="emergency-section">
        <div className="container">
          {/* Partners strip */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="pillar-card">
                <h4 className="mb-3">Implementation Partners</h4>
                <ul className="pillar-highlights">
                  <li>
                    <Home size={16} /> Rakai District Health Office (coordination)
                  </li>
                  <li>
                    <Home size={16} /> Masaka Regional Referral Hospital
                    (diagnostics & referral)
                  </li>
                  <li>
                    <Home size={16} /> Uganda Cancer Institute (tertiary care)
                  </li>
                  <li>
                    <Home size={16} /> Local media & CBOs (mobilization & awareness)
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Budget Section */}
      <section className="budget-section">
        <div className="container">
          <div className="budget-card">
            <h3 className="budget-title">Budget Summary (Indicative)</h3>
            <p className="budget-subtitle">
              Project duration: 24 months • Implementer: RACOHI
            </p>

            <div className="table-responsive">
              <table className="table budget-table align-middle">
                <thead>
                  <tr>
                    <th scope="col">Budget Category</th>
                    <th scope="col" className="text-end">
                      Amount (EUR)
                    </th>
                    <th scope="col" className="text-end">
                      % of Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Community awareness and education</td>
                    <td className="text-end">€22,000</td>
                    <td className="text-end">18.3%</td>
                  </tr>
                  <tr>
                    <td>Screening equipment and medical supplies</td>
                    <td className="text-end">€35,000</td>
                    <td className="text-end">29.2%</td>
                  </tr>
                  <tr>
                    <td>Training of health workers and CHWs</td>
                    <td className="text-end">€15,000</td>
                    <td className="text-end">12.5%</td>
                  </tr>
                  <tr>
                    <td>Referral support and logistics</td>
                    <td className="text-end">€20,000</td>
                    <td className="text-end">16.7%</td>
                  </tr>
                  <tr>
                    <td>Monitoring and evaluation</td>
                    <td className="text-end">€10,000</td>
                    <td className="text-end">8.3%</td>
                  </tr>
                  <tr>
                    <td>Administration and coordination</td>
                    <td className="text-end">€15,000</td>
                    <td className="text-end">12.5%</td>
                  </tr>
                  <tr className="table-light">
                    <td>
                      <strong>Contingency / Unallocated</strong>
                    </td>
                    <td className="text-end">
                      <strong>€3,000</strong>
                    </td>
                    <td className="text-end">
                      <strong>2.5%</strong>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row">Total Project Budget</th>
                    <th className="text-end">€120,000</th>
                    <th className="text-end">100%</th>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* simple visual bars */}
            <div className="budget-bars">
              <div className="bar">
                <span>Awareness & education</span>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: "18.3%" }} />
                </div>
                <strong>18.3%</strong>
              </div>
              <div className="bar">
                <span>Equipment & supplies</span>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: "29.2%" }} />
                </div>
                <strong>29.2%</strong>
              </div>
              <div className="bar">
                <span>Training (HWs & CHWs)</span>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: "12.5%" }} />
                </div>
                <strong>12.5%</strong>
              </div>
              <div className="bar">
                <span>Referral & logistics</span>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: "16.7%" }} />
                </div>
                <strong>16.7%</strong>
              </div>
              <div className="bar">
                <span>M&E</span>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: "8.3%" }} />
                </div>
                <strong>8.3%</strong>
              </div>
              <div className="bar">
                <span>Admin & coordination</span>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: "12.5%" }} />
                </div>
                <strong>12.5%</strong>
              </div>
              <div className="bar">
                <span>Contingency</span>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: "2.5%" }} />
                </div>
                <strong>2.5%</strong>
              </div>
            </div>

            <p className="budget-note">
              <span>Note:</span> Your listed line items equal{" "}
              <strong>€117,000</strong>. We’ve added a{" "}
              <em>€3,000 contingency</em> to reconcile to the stated total of
              <strong> €120,000</strong>. Rename/remove that row if you’d prefer
              a different split.
            </p>
          </div>
        </div>
      </section>

      {/* Styles */}
      <style jsx>{`
        :root {
          --primary-color: #06b6d4;
          --primary-dark: #0891b2;
          --primary-light: #67e8f9;
          --accent-orange: #f59e0b;
          --text-dark: #1f2937;
          --text-gray: #6b7280;
          --text-light: #9ca3af;
          --white: #ffffff;
          --light-bg: #f8fafc;
          --border-light: rgba(0, 0, 0, 0.06);
          --success-green: #10b981;
          --warning-red: #ef4444;
        }

        /* Urgent Banner */
        .urgent-banner {
          background: #dc2626;
          color: #fff;
          padding: 10px 0;
        }
        .urgent-content {
          display: flex;
          align-items: center;
          gap: 12px;
          justify-content: center;
          text-align: center;
        }
        .urgent-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.18);
          border-radius: 999px;
          padding: 6px;
        }
        .urgent-text {
          margin: 0;
          font-size: 0.98rem;
          line-height: 1.4;
          font-weight: 500;
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
            sans-serif;
          line-height: 1.6;
          color: var(--text-dark);
          padding-top: 80px;
        }

        /* Hero */
        .hero-section {
          position: relative;
          min-height: 85vh;
          display: flex;
          align-items: center;
          overflow: hidden;
          padding: 25px 0;
        }
        .hero-background {
          position: absolute; inset: 0;
          background: linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%);
        }
        .hero-overlay {
          position: absolute; inset: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="20" cy="20" r="2" fill="rgba(6,182,212,0.05)"/><circle cx="80" cy="80" r="1.5" fill="rgba(6,182,212,0.03)"/></svg>')
            repeat;
        }
        .hero-content { position: relative; z-index: 2; padding-right: 2rem; }
        .hero-badge {
          display: inline-block; padding: 8px 20px;
          background: rgba(6,182,212,0.1); color: var(--primary-color);
          border-radius: 50px; font-size: 14px; font-weight: 600;
          margin-bottom: 24px; text-transform: uppercase; letter-spacing: 0.5px;
        }
        .hero-title {
          font-size: clamp(2.5rem, 4vw, 4rem);
          font-weight: 800; color: var(--text-dark); margin-bottom: 12px;
          line-height: 1.1;
        }
        .hero-subtitle {
          font-size: 1.5rem; color: var(--primary-color); font-weight: 600;
          margin-bottom: 24px;
        }
        .hero-description {
          font-size: 1.2rem; color: var(--text-gray); margin-bottom: 40px;
          line-height: 1.6; max-width: 600px;
        }

        .hero-image-container { position: relative; padding-left: 2rem; }
        .project-image {
          position: relative; width: 100%; height: 420px;
          border-radius: 20px; overflow: hidden;
          box-shadow: 0 20px 60px rgba(0,0,0,0.15);
          background: #e6f9fc;
        }
        .project-image.gate .hero-image.blurred {
          filter: blur(14px) saturate(0.8) brightness(0.9);
          transform: scale(1.02);
        }
        .hero-image { width: 100%; height: 100%; object-fit: cover; }
        .image-placeholder {
          display: none; height: 100%;
          align-items: center; justify-content: center; flex-direction: column;
          background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
          color: #fff; gap: 12px;
        }

        /* Content Warning overlay */
        .content-warning {
          position: absolute; inset: 0;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          padding: 24px; text-align: center; gap: 10px;
          background: linear-gradient(180deg, rgba(15,23,42,0.65), rgba(15,23,42,0.35));
          color: #fff;
        }
        .cw-icon {
          width: 46px; height: 46px; border-radius: 999px;
          background: rgba(255,255,255,0.15);
          display: inline-flex; align-items: center; justify-content: center;
        }
        .cw-title { margin: 8px 0 6px; font-weight: 800; letter-spacing: 0.2px; }
        .cw-desc { margin: 0 0 12px; opacity: 0.95; }
        .cw-actions .btn { font-weight: 700; }

        /* Re-hide button */
        .rehide {
          position: absolute; top: 10px; right: 10px;
          background: rgba(0,0,0,0.55); color: #fff; border: 0;
          padding: 6px 10px; border-radius: 8px; font-weight: 600;
        }
        .rehide:hover { background: rgba(0,0,0,0.7); }

        /* Overview */
        .overview-section { padding: 100px 0; background: var(--light-bg); }
        .project-image-container { position: relative; }
        .project-overview-image {
          width: 100%; height: 500px; object-fit: cover;
          border-radius: 20px; box-shadow: 0 20px 60px rgba(0,0,0,0.15);
        }
        .overview-content { padding-left: 48px; }
        .section-badge {
          display: inline-block; padding: 6px 16px;
          background: rgba(6,182,212,0.1); color: var(--primary-color);
          border-radius: 50px; font-size: 12px; font-weight: 600;
          margin-bottom: 20px; text-transform: uppercase; letter-spacing: 0.5px;
        }
        .section-title {
          font-size: clamp(2rem, 3vw, 2.5rem); font-weight: 700;
          color: var(--text-dark); margin-bottom: 24px; line-height: 1.2;
        }
        .section-description { font-size: 1.1rem; color: var(--text-gray); margin-bottom: 40px; line-height: 1.6; }

        .impact-areas { margin-top: 48px; }
        .subsection-title { font-size: 1.3rem; font-weight: 600; color: var(--text-dark); margin-bottom: 24px; }
        .impact-item { margin-bottom: 24px; padding-bottom: 24px; border-bottom: 1px solid var(--border-light); }
        .impact-item:last-child { border-bottom: none; }
        .impact-title { font-size: 1.1rem; font-weight: 600; color: var(--primary-color); margin-bottom: 8px; }
        .impact-description { color: var(--text-gray); margin: 0; }

        /* Pillars */
        .pillars-section { padding: 100px 0; background: #fff; }
        .section-header { margin-bottom: 80px; opacity: 0; transform: translateY(30px); transition: all 0.8s ease; }
        .section-header.animate-in { opacity: 1; transform: translateY(0); }
        .pillar-card {
          background: #fff; border-radius: 20px; padding: 40px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.08); transition: all 0.3s ease;
          height: 100%; border: 1px solid var(--border-light);
        }
        .pillar-card:hover { transform: translateY(-8px); box-shadow: 0 16px 48px rgba(0,0,0,0.12); }
        .pillar-icon {
          width: 80px; height: 80px; background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
          border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; margin-bottom: 24px;
        }
        .pillar-title { font-size: 1.5rem; font-weight: 700; color: var(--text-dark); margin-bottom: 16px; }
        .pillar-description { color: var(--text-gray); margin-bottom: 24px; line-height: 1.6; }
        .pillar-highlights { list-style: none; padding: 0; margin: 0; }
        .pillar-highlights li {
          display: flex; align-items: center; gap: 12px; margin-bottom: 12px;
          color: var(--text-gray); font-size: 0.95rem;
        }

        /* Emergency */
        .emergency-section { padding: 80px 0; background: var(--light-bg); }
        .emergency-card {
          background: linear-gradient(135deg, var(--warning-red), #dc2626);
          border-radius: 20px; padding: 60px; color: #fff; text-align: center;
        }
        .emergency-content {
          display: flex; align-items: center; gap: 40px; max-width: 800px; margin: 0 auto;
        }
        .emergency-icon {
          background: rgba(255,255,255,0.2); border-radius: 50%; padding: 24px; flex-shrink: 0;
        }
        .emergency-title { font-size: 2rem; font-weight: 700; margin-bottom: 16px; }
        .emergency-description { font-size: 1.1rem; opacity: 0.9; margin-bottom: 32px; }
        .emergency-actions { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; }

        /* Budget */
        .budget-section { padding: 80px 0; background: var(--light-bg); }
        .budget-card {
          background: #fff; border-radius: 20px; padding: 40px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.08); border: 1px solid var(--border-light);
        }
        .budget-title { font-size: clamp(1.6rem, 2.5vw, 2rem); font-weight: 700; margin-bottom: 6px; color: var(--text-dark); }
        .budget-subtitle { color: var(--text-gray); margin-bottom: 24px; }
        .budget-table thead th { border-bottom: 2px solid var(--border-light); color: var(--text-dark); }
        .budget-table tfoot th, .budget-table tfoot td { border-top: 2px solid var(--border-light); font-weight: 700; }

        .budget-bars { display: grid; grid-template-columns: 1fr; gap: 12px; margin-top: 20px; }
        .bar { display: grid; grid-template-columns: 240px 1fr 70px; gap: 12px; align-items: center; }
        .bar span { color: var(--text-dark); font-weight: 500; }
        .bar strong { text-align: right; color: var(--text-dark); }
        .bar-track { height: 10px; border-radius: 999px; background: #eef2f7; overflow: hidden; border: 1px solid var(--border-light); }
        .bar-fill {
          height: 100%; width: 0%;
          background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
          border-radius: 999px; transition: width 0.8s ease;
        }

        /* Buttons */
        .btn { padding: 16px 32px; border-radius: 12px; font-weight: 600; text-decoration: none; transition: all 0.3s ease; border: 2px solid transparent; font-size: 1rem; }
        .btn-outline-light:hover { background: #fff; color: var(--primary-color); transform: translateY(-2px); }
        .btn-primary { background: var(--accent-orange); border-color: var(--accent-orange); color: #fff; }
        .btn-primary:hover { background: #d97706; border-color: #d97706; transform: translateY(-2px); box-shadow: 0 8px 24px rgba(245,158,11,0.3); }

        /* Responsive */
        @media (max-width: 1200px) {
          .overview-content { padding-left: 24px; }
          .hero-content { padding-right: 1rem; }
          .hero-image-container { padding-left: 1rem; }
        }
        @media (max-width: 992px) {
          .overview-content { padding-left: 0; margin-top: 48px; }
          .hero-content { padding-right: 0; margin-bottom: 48px; }
          .hero-image-container { padding-left: 0; }
          .emergency-content { flex-direction: column; text-align: center; }
        }
        @media (max-width: 768px) {
          .hero-section { padding: 20px 0; }
          .pillar-card { padding: 32px 24px; }
          .emergency-card { padding: 60px 24px; }
          .btn { padding: 14px 28px; font-size: 0.95rem; }
        }

        /* Animation */
        .animate-in { animation: slideInUp 0.8s ease-out forwards; }
        @keyframes slideInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default CancerProject;
